<script setup lang="ts">
import { computed, ref } from 'vue'
import zhCN from 'ant-design-vue/es/locale/zh_CN'
import { theme } from 'ant-design-vue'
import { RouterView, useRoute } from 'vue-router'
import { storeToRefs } from 'pinia'
import MainLayout from '@/components/layout/MainLayout.vue'
import ExportConfirmModal from '@/components/ExportConfirmModal.vue'
import { useThemeStore } from '@/stores/theme'
import router from '@/router'

const bootReady = ref(false)
void router.isReady().then(() => {
  bootReady.value = true
})

const route = useRoute()
const themeStore = useThemeStore()
const { primaryColor, isDark } = storeToRefs(themeStore)

const antdTheme = computed(() => {
  const primary = primaryColor.value
  return {
    algorithm: isDark.value ? theme.darkAlgorithm : theme.defaultAlgorithm,
    token: {
      colorPrimary: primary,
      colorLink: primary,
      colorLinkHover: isDark.value ? '#38bdf8' : '#0284c7',
      colorLinkActive: isDark.value ? '#0ea5e9' : '#0369a1',
      colorBgLayout: isDark.value ? '#141414' : '#f8fafc',
      colorBgContainer: isDark.value ? '#1f1f1f' : '#ffffff',
      colorBgElevated: isDark.value ? '#262626' : '#ffffff',
      colorText: isDark.value ? 'rgba(255,255,255,0.85)' : '#020617',
      colorTextSecondary: isDark.value ? 'rgba(255,255,255,0.65)' : '#475569',
      colorTextTertiary: isDark.value ? 'rgba(255,255,255,0.45)' : '#64748b',
      colorBorder: isDark.value ? '#303030' : '#e2e8f0',
      colorBorderSecondary: isDark.value ? '#262626' : '#f1f5f9',
      borderRadius: 8,
      borderRadiusLG: 12,
      borderRadiusSM: 6,
      controlHeight: 36,
      fontFamily:
        "'Noto Sans SC', 'Source Han Sans SC', '思源黑体', -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: 14,
      lineHeight: 1.5715,
      motionDurationMid: '0.22s',
      motionEaseInOut: 'cubic-bezier(0.16, 1, 0.3, 1)',
      controlOutline: `2px solid ${isDark.value ? 'rgba(56, 189, 248, 0.35)' : 'rgba(3, 105, 161, 0.28)'}`,
      controlOutlineWidth: 2,
      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.05)',
      boxShadowSecondary: '0 4px 16px rgba(15, 23, 42, 0.06)',
    },
    components: {
      Table: {
        headerBg: isDark.value ? '#1f1f1f' : '#f8fafc',
        headerColor: isDark.value ? 'rgba(255,255,255,0.85)' : '#334155',
        rowHoverBg: isDark.value ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
        borderColor: isDark.value ? '#303030' : '#f1f5f9',
      },
      Card: {
        borderRadiusLG: 12,
      },
      Button: {
        borderRadius: 8,
        controlHeight: 36,
        colorPrimary: primary,
        colorPrimaryHover: isDark.value ? '#38bdf8' : '#0284c7',
        colorPrimaryActive: isDark.value ? '#0ea5e9' : '#0369a1',
        defaultGhostColor: primary,
        defaultGhostBorderColor: isDark.value
          ? 'rgba(56, 189, 248, 0.55)'
          : 'rgba(3, 105, 161, 0.45)',
      },
      Modal: {
        borderRadiusLG: 12,
      },
      Menu: {
        itemBorderRadius: 10,
        itemHeight: 44,
        itemMarginBlock: 2,
        itemMarginInline: 8,
        iconSize: 16,
        iconMarginInlineEnd: 10,
        subMenuItemBg: 'transparent',
        itemColor: isDark.value ? 'rgba(255,255,255,0.72)' : '#334155',
        itemHoverColor: isDark.value ? '#38bdf8' : primary,
        itemSelectedColor: isDark.value ? '#38bdf8' : primary,
        itemHoverBg: isDark.value ? 'rgba(255,255,255,0.06)' : '#f1f5f9',
        itemSelectedBg: isDark.value ? 'rgba(56, 189, 248, 0.12)' : 'rgba(3, 105, 161, 0.1)',
      },
    },
  }
})

/** 登录页、403、404、大屏 为全屏，不套主布局 */
const useFullPageShell = () =>
  ['login', 'forbidden', 'not-found'].includes(route.name as string) ||
  String(route.path || '').startsWith('/dashboard-screen')
</script>

<template>
  <a-config-provider :locale="zhCN" :theme="antdTheme">
    <template v-if="bootReady">
      <MainLayout v-if="!useFullPageShell()">
        <RouterView />
      </MainLayout>
      <RouterView v-else />
      <ExportConfirmModal />
    </template>
    <div v-else class="app-boot-splash" aria-busy="true" aria-live="polite">
      <div class="app-boot-splash-inner">
        <a-spin size="large" />
        <p class="app-boot-splash-text">加载中...</p>
      </div>
    </div>
  </a-config-provider>
</template>

<style scoped>
.app-boot-splash {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: var(--layout-main-bg);
}

.app-boot-splash-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.app-boot-splash-text {
  margin: 0;
  color: var(--color-text);
  font-size: 15px;
  opacity: 0.65;
}
</style>
